import { getPreviousStackEntry } from '~~/layers/base/utils/navigationStackEntry'

export const useNavigationStackStore = defineStore('navigationStack', () => {
    const runtimeConfig = useRuntimeConfig() as { public?: { NUXT_CAPACITOR_APP?: boolean } }
    const isCapacitorApp = computed(() => Boolean(runtimeConfig.public?.NUXT_CAPACITOR_APP))

    const stack = ref<{ paths: string[]; name: string }[]>([])
    const currentStack = computed(() => stack.value[stack.value.length - 1] || null)
    const currentStackName = computed(() => currentStack.value?.name || null)
    const currentStackPaths = computed(() => currentStack.value?.paths || [])
    const currentStackPath = computed(
        () => currentStackPaths.value[currentStackPaths.value.length - 1] || null
    )
    /** Most recently used prior stack (`stack[length - 2]`); used for Send stack-exit back (ADR §11). */
    const previousStack = computed(() => getPreviousStackEntry(stack.value))
    /**
     * One-shot slide direction for the next navigation (consumed by Capacitor transition middleware).
     * Set by tab taps, explicit backs, and child-route saves when path ancestry alone is ambiguous.
     */
    const pendingPageTransition = ref<'page' | 'page-reverse' | null>(null)

    const setPendingPageTransition = (transition: 'page' | 'page-reverse') => {
        pendingPageTransition.value = transition
    }

    const consumePendingPageTransition = (): 'page' | 'page-reverse' | null => {
        const value = pendingPageTransition.value
        pendingPageTransition.value = null
        return value
    }

    interface PathOptions {
        replace?: boolean
    }

    /**
     * Pushes `path` onto the named stack (creates or switches to it first).
     * Pass `(path, name)` or `(path, opts, name)` so `name` stays last for a future default.
     * If `replace` is true, the last path is replaced; otherwise the path is appended.
     * Returns the path if pushed, or null when not in the Capacitor app.
     */
    const pushToStack = (path: string, optsOrName?: PathOptions | string, name?: string) => {
        if (!isCapacitorApp.value) return null
        const stackName = typeof optsOrName === 'string' ? optsOrName : name
        const opts = typeof optsOrName === 'string' ? undefined : optsOrName

        const stackObj = stackName ? createOrSwitchToStack(stackName) : currentStack.value
        if (stackObj) {
            if (opts?.replace) {
                stackObj.paths.splice(stackObj.paths.length - 1, 1, path)
            } else {
                stackObj.paths.push(path)
            }
            return path
        }
        return null
    }

    /**
     * Pops the last path from the named stack.
     * Returns the new top path, `'/'` if empty, or null when not in the Capacitor app.
     * TODO: should this return the next stack in the array instead of '/' if the stack is empty?
     */
    const popFromStack = (name: string) => {
        if (!isCapacitorApp.value) return null
        const stackObj = getStack(name)
        if (stackObj) {
            stackObj.paths.pop()
            return stackObj.paths[stackObj.paths.length - 1] || '/'
        }
        return null
    }

    /**
     * Returns the stack with `name`, or null if not found or not in the Capacitor app.
     */
    const getStack = (name: string) => {
        if (!isCapacitorApp.value) return null
        const stackObj = stack.value.find((s) => s.name === name)
        if (!stackObj) {
            return null
        }
        return stackObj || null
    }

    /**
     * Creates a new stack if it does not exist, or switches to the existing stack if it does.
     * SIDE EFFECT: Moves the stack to the end of the array if it already exists.
     * Returns the stack object.
     * If not a Capacitor app, early returns null.
     */
    const createOrSwitchToStack = (name: string, path?: string) => {
        if (!isCapacitorApp.value) return null
        const stackObj = getStack(name)
        if (stackObj) {
            // If stack obj is found, switch to it by moving it to the end of the array
            stack.value.splice(stack.value.indexOf(stackObj), 1)
            stack.value.push(stackObj)
        } else {
            // If stack obj is not found, create it
            stack.value.push({ name, paths: path ? [path] : [] })
        }

        return stack.value[stack.value.length - 1]
    }

    /**
     * Starts a fresh stack named `name` with initial `path` (removes any prior stack with that name).
     * If not a Capacitor app, early returns null.
     */
    const startStack = (path: string, name: string) => {
        if (!isCapacitorApp.value) return null
        const stackObj = getStack(name)
        if (stackObj) {
            // If stack obj is found, remove it before pushing the new stack
            stack.value.splice(stack.value.indexOf(stackObj), 1)
        }
        stack.value.push({ name, paths: [path] })

        return stack.value[stack.value.length - 1]
    }

    /**
     * Closes a stack, if it exists.
     * Returns the last stack object in the array.
     * If not a Capacitor app, early returns null.
     */
    const closeStack = (name: string) => {
        if (!isCapacitorApp.value) return null
        const stackObj = getStack(name)
        if (stackObj) {
            // If stack obj is found, remove it from the array
            stack.value.splice(stack.value.indexOf(stackObj), 1)
        }
        return stack.value[stack.value.length - 1]
    }

    /**
     * Wipes every stack and leaves only `home` at `/`.
     * Used by terminal order-success close so funnel history cannot be re-entered via back.
     * If not a Capacitor app, early returns null.
     */
    const resetAllStacksToHome = () => {
        if (!isCapacitorApp.value) return null

        stack.value = [{ name: 'home', paths: ['/'] }]
        return stack.value[0]
    }

    return {
        currentStack,
        currentStackPath,
        currentStackPaths,
        currentStackName,
        previousStack,
        stack,
        closeStack,
        createOrSwitchToStack,
        getStack,
        consumePendingPageTransition,
        setPendingPageTransition,
        popFromStack,
        pushToStack,
        resetAllStacksToHome,
        startStack,
    }
})
